// Layer 4 (UI): polish.P9 (#657) — the stage's balance in the 4-column panel
// beside the flowsheet (locked comp `metal-accounting.html` A, `stagePanel`):
// hairline fact rows (label left, figure right, a muted line under), caps
// section labels, the stage row with the open case on the state rule. Renders
// `stagePanelView` verbatim; the case links open the case drawer, as the old
// node detail's did.
//
// r2: the header is the house panel header (title class, hairline) with the
// title wrapping in its own column beside *Open in Advisory* (two lines at
// most, the full name in its tooltip), so the link stays on the title line. `ghost` renders the same panel invisible, with no ids, anchors or
// handlers: the Scene stacks every stage's panel in one grid cell so the row
// holds the tallest one's height and nothing jumps when the stage changes.

import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_SECTION_TITLE } from '@/shared/ui-class';
import { Commentable } from '@/shared/comments/Commentable';
import { useDrawer } from '@/app/providers';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { STAGE_INK_LINK, STAGE_LABEL_CAPS } from '@/versions/v1/modules/advisory/stage-body-ui';
import type { PanelFact, PanelRun, StagePanelView } from './StagePanel.transform';

function Runs({ runs }: { runs: readonly PanelRun[] }) {
  return (
    <>
      {runs.map((run, index) => (
        <span
          key={index}
          className={cn(run.block ? 'block' : undefined, run.tone && run.tone !== 'neutral' ? 'font-semibold' : undefined)}
          style={run.tone && run.tone !== 'neutral' ? { color: vizToneColor(run.tone) } : undefined}
        >
          {run.text}
        </span>
      ))}
    </>
  );
}

function FactRow({
  fact,
  onOpenCase,
  ghost,
}: {
  fact: PanelFact;
  onOpenCase: (caseId: string, title: string) => void;
  ghost: boolean;
}) {
  return (
    <div
      data-stage-fact={ghost ? undefined : fact.id}
      className={cn(
        'py-1.5 text-[13px]',
        fact.flagged ? 'pl-2.5 shadow-[inset_3px_0_0_var(--state-blocked)]' : '',
      )}
    >
      {/* Label left, figure right. A figure too long to sit beside its label
          (`0.1 dst Pb · +0.3 dst Zn · +1 oz Ag`) wraps whole to its own line,
          right-aligned, one metal per unbroken run, instead of breaking
          mid-figure in a narrow column. */}
      <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-ink-secondary">{fact.label}</span>
        <span
          className="ml-auto min-w-0 text-right font-semibold tabular-nums text-ink-primary"
          style={fact.valueTone && fact.valueTone !== 'neutral' ? { color: vizToneColor(fact.valueTone) } : undefined}
        >
          {fact.value.split(' · ').map((part, index) => (
            <span key={index} className="whitespace-nowrap">
              {index > 0 ? ' · ' : null}
              {part}
            </span>
          ))}
        </span>
      </span>
      {fact.sub.length > 0 || fact.action ? (
        <span className="mt-0.5 block text-xs leading-snug text-muted">
          <Runs runs={fact.sub} />
          {fact.action ? (
            <>
              {fact.sub.length > 0 ? (fact.action.tone === 'warn' ? ' · ' : <br />) : null}
              <button
                type="button"
                tabIndex={ghost ? -1 : undefined}
                onClick={ghost ? undefined : () => onOpenCase(fact.action!.caseId, fact.action!.drawerTitle)}
                className={cn(
                  'text-left',
                  fact.action.tone === 'warn'
                    ? 'font-semibold text-[var(--state-warning)] hover:underline'
                    : cn(STAGE_INK_LINK, 'font-semibold !text-ink-primary'),
                )}
              >
                {fact.action.label}
                {fact.action.tone === 'warn' ? null : ' ›'}
              </button>
            </>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function StagePanel({
  panel,
  advisoryHref,
  className,
  ghost = false,
}: {
  panel: StagePanelView;
  advisoryHref: string | null;
  className?: string;
  /** Size-only copy: invisible, hidden from assistive tech, no ids or handlers. */
  ghost?: boolean;
}) {
  const { open: openDrawer } = useDrawer();
  const openCase = (caseId: string, title: string) =>
    openDrawer('case-drawer', { kicker: 'Case', title, wide: true, data: { caseId } });

  const body = (
    <section
      className={cn('card-matte-2 flex min-w-0 flex-col', ghost ? 'invisible' : undefined, className)}
      aria-hidden={ghost || undefined}
    >
      <div className="hairline-b flex items-baseline justify-between gap-3 px-4 pb-2.5 pt-3">
        <h2 className={cn(SURFACE_PAGE_SECTION_TITLE, 'min-w-0 line-clamp-2 leading-snug')} title={panel.title}>
          {panel.title}
        </h2>
        {advisoryHref ? (
          ghost ? (
            <span className={cn(STAGE_INK_LINK, 'shrink-0 whitespace-nowrap font-semibold')}>Open in Advisory ›</span>
          ) : (
            <Link to={advisoryHref} className={cn(STAGE_INK_LINK, 'shrink-0 whitespace-nowrap font-semibold !text-ink-primary')}>
              Open in Advisory ›
            </Link>
          )
        ) : null}
      </div>
      <div className="min-h-0 flex-1 px-4 py-3">
        <div
          {...(ghost
            ? {}
            : {
                id: 'balance-stage-panel',
                role: 'tabpanel',
                'aria-labelledby': `balance-stage-${panel.stage}`,
                'data-stage-panel': panel.stage,
              })}
          className="gap-x-8 md:columns-2 min-[1400px]:columns-1"
        >
          {panel.sections.map((section, index) => (
            <div key={section.id} className={cn('break-inside-avoid', index > 0 ? 'mt-3' : undefined)}>
              {section.label ? <p className={cn(STAGE_LABEL_CAPS, 'text-[10.5px]')}>{section.label}</p> : null}
              {section.basis ? <p className="mt-0.5 text-xs leading-snug text-muted">{section.basis}</p> : null}
              <div className="divide-y divide-divider">
                {section.facts.map((fact) => (
                  <FactRow key={fact.id} fact={fact} onOpenCase={openCase} ghost={ghost} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
  return ghost ? body : <Commentable name="NodeDetail">{body}</Commentable>;
}
